import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, ModalHeader, ModalBody, ModalFooterWithButtons } from '@evertrue/et-components';
import Api from 'entities/helpers/api';
import usePortfolioTeams from '../../../hooks/use-portfolio-teams';
import SearchableSelect from '../../searchable-select';

/**
 * Condition types for cadence branching, grouped by category.
 */
const CONDITION_GROUPS = [
  {
    category: 'Email',
    conditions: [
      {
        id: 'email_opened',
        label: 'Email Opened?',
        description: 'Check if the constituent opened the previous email',
      },
      {
        id: 'email_replied',
        label: 'Responded to Email?',
        description: 'Check if the constituent replied to the previous email',
      },
      {
        id: 'email_clicked',
        label: 'Email Link Clicked?',
        description: 'Check if the constituent clicked a link in the previous email',
      },
    ],
  },
  {
    category: 'Activity',
    conditions: [
      {
        id: 'interaction_created',
        label: 'Had Interaction',
        description: 'Check for a specific interaction type since the previous step',
        hasOptions: true,
        optionType: 'interaction',
      },
      {
        id: 'gift_given',
        label: 'Made Gift',
        description: 'Check if the constituent made a gift since the previous step',
        hasOptions: true,
        optionType: 'gift',
      },
    ],
  },
  {
    category: 'Portfolio',
    conditions: [
      {
        id: 'portfolio_stage_reached',
        label: 'Moved into Portfolio Stage',
        description: 'Check if the constituent is currently in a specific portfolio stage',
        hasOptions: true,
        optionType: 'portfolio',
      },
    ],
  },
  {
    category: 'Segment',
    conditions: [
      {
        id: 'saved_search',
        label: 'Qualifies for Saved Search',
        description: 'Check if the constituent currently matches a saved search',
        hasOptions: true,
        optionType: 'savedSearch',
      },
      {
        id: 'list_membership',
        label: 'Is in List',
        description: 'Check if the constituent is currently in a list',
        hasOptions: true,
        optionType: 'list',
      },
    ],
  },
];

const EMAIL_CONDITION_IDS = new Set(['email_opened', 'email_replied', 'email_clicked']);

/**
 * Look up a CONDITION_GROUPS entry by its `id` (the saved condition_type).
 */
const findConditionById = (id) => {
  for (const group of CONDITION_GROUPS) {
    const match = group.conditions.find((c) => c.id === id);
    if (match) return match;
  }
  return null;
};

/**
 * Modal for adding a condition to the cadence flow.
 *
 * When `existingCondition` is supplied, the modal opens in edit mode with
 * the type radio pre-selected and the option-specific picker pre-populated
 * (NEXDEV-905 — previously, editing a saved condition opened a blank modal
 * because internal state always reset to null).
 */
const AddConditionModal = ({ isOpen, onClose, onAddCondition, previousStepType, existingCondition = null }) => {
  const emailConditionsDisabled = previousStepType !== 'email';
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [interactionType, setInteractionType] = useState(null);
  const [minAmount, setMinAmount] = useState('');
  const [designation, setDesignation] = useState('');
  const [portfolioTeam, setPortfolioTeam] = useState('');
  const [portfolioStage, setPortfolioStage] = useState('');
  const [portfolioStageName, setPortfolioStageName] = useState('');
  const [savedSearch, setSavedSearch] = useState(null);
  const [list, setList] = useState(null);

  const [interactionTypes, setInteractionTypes] = useState([]);
  const [portfolioStages, setPortfolioStages] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    teams: portfolioTeamsList,
    getStages: getPortfolioStages,
    loading: portfolioTeamsLoading,
  } = usePortfolioTeams();

  // Load per-team stages when team selection changes
  useEffect(() => {
    if (!portfolioTeam) {
      setPortfolioStages([]);
      setPortfolioStage('');
      setPortfolioStageName('');
      return;
    }
    getPortfolioStages(portfolioTeam).then((stages) => {
      setPortfolioStages((stages || []).map((s) => ({ label: s.name, value: s.id, name: s.name })));
    });
  }, [portfolioTeam, getPortfolioStages]);

  // Seed type + scalar fields from existingCondition every time the modal
  // opens. Async fields (savedSearch, list) are seeded in a second effect
  // that waits for their option lists to load — the option needs a matching
  // entry by value before SearchableSelect will render it as selected.
  useEffect(() => {
    if (!isOpen) return;
    if (!existingCondition?.type) {
      setSelectedCondition(null);
      return;
    }
    const matched = findConditionById(existingCondition.type);
    setSelectedCondition(matched);
    setInteractionType(
      existingCondition.interactionType
        ? { label: existingCondition.interactionType, value: existingCondition.interactionType }
        : null
    );
    setMinAmount(existingCondition.minAmount || '');
    setDesignation(existingCondition.designation || '');
    setPortfolioTeam(existingCondition.portfolioTeamId || '');
    setPortfolioStage(existingCondition.portfolioStage || '');
    setPortfolioStageName(existingCondition.portfolioStage || '');
    // savedSearch / list are seeded once their option lists load (effects below).
  }, [isOpen, existingCondition]);

  // Pre-select savedSearch option once savedSearches finishes loading.
  useEffect(() => {
    if (!isOpen || !existingCondition?.savedSearch || savedSearches.length === 0) return;
    const match = savedSearches.find((s) => String(s.value) === String(existingCondition.savedSearch));
    if (match) setSavedSearch(match);
  }, [isOpen, existingCondition, savedSearches]);

  // Pre-select list option once lists finishes loading.
  useEffect(() => {
    if (!isOpen || !existingCondition?.list || lists.length === 0) return;
    const match = lists.find((l) => String(l.value) === String(existingCondition.list));
    if (match) setList(match);
  }, [isOpen, existingCondition, lists]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);

    Promise.all([
      Api.UGC.INTERACTION_TYPES.get()
        .then((resp) => {
          const items = (resp || [])
            .filter((type) => type && type !== 'EverTrue Comment')
            .map((type) => ({
              label: type,
              value: type,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          setInteractionTypes(items);
        })
        .catch(() => setInteractionTypes([])),
      Api.CONTACTS.LISTS.get({ params: { limit: 1000, type: 'dynamic' } })
        .then((resp) => {
          const items = (resp?.items || [])
            .filter((item) => item.tag_id)
            .map((item) => ({
              label: item.name,
              value: String(item.tag_id),
              // Contacts list id — needed downstream so the condition writes
              // saved_search_list_id into event_config (powers the cadences
              // real-time _explain matcher per NEXDEV-905).
              listId: item.id || null,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          setSavedSearches(items);
        })
        .catch(() => setSavedSearches([])),
      Api.CONTACTS.LISTS.get({ params: { limit: 1000, type: 'user_list' } })
        .then((resp) => {
          const items = (resp?.items || [])
            .filter((item) => item.id && (item.type === 'user_list' || item.type === 1))
            .map((item) => ({
              label: item.name,
              value: String(item.id),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          setLists(items);
        })
        .catch(() => setLists([])),
    ]).finally(() => setLoading(false));
  }, [isOpen]);

  const handleAddCondition = () => {
    if (!selectedCondition) return;

    const condition = {
      type: selectedCondition.id,
      label: selectedCondition.label,
    };

    if (selectedCondition.optionType === 'interaction') {
      condition.interactionType = interactionType?.value || '';
    } else if (selectedCondition.optionType === 'gift') {
      condition.minAmount = minAmount;
      condition.designation = designation;
    } else if (selectedCondition.optionType === 'portfolio') {
      condition.portfolioTeamId = portfolioTeam;
      condition.portfolioStage = portfolioStageName;
      const matchedTeam = (portfolioTeamsList || []).find((t) => String(t.id) === String(portfolioTeam));
      condition.portfolioTeamName = matchedTeam?.name || '';
    } else if (selectedCondition.optionType === 'savedSearch') {
      condition.savedSearch = savedSearch?.value || '';
      condition.savedSearchName = savedSearch?.label || '';
      // Carry the contacts list_id so backend can refresh tag membership
      // before metric/condition evaluation. May be null on older picker rows.
      condition.savedSearchListId = savedSearch?.listId ?? null;
    } else if (selectedCondition.optionType === 'list') {
      condition.list = list?.value || '';
      condition.listName = list?.label || '';
    }

    onAddCondition(condition);
    handleClose();
  };

  const handleClose = () => {
    setSelectedCondition(null);
    setInteractionType(null);
    setMinAmount('');
    setDesignation('');
    setPortfolioTeam('');
    setPortfolioStage('');
    setPortfolioStageName('');
    setSavedSearch(null);
    setList(null);
    onClose();
  };

  const renderConditionOptions = () => {
    if (!selectedCondition || !selectedCondition.hasOptions) return null;

    switch (selectedCondition.optionType) {
      case 'interaction':
        return (
          <SearchableSelect
            options={interactionTypes}
            value={interactionType}
            onChange={setInteractionType}
            placeholder={loading ? 'Loading...' : 'Select interaction type...'}
            className="add-condition-modal--searchable-select"
          />
        );

      case 'gift':
        return (
          <div className="add-condition-modal--gift-options">
            <div className="add-condition-modal--gift-field">
              <label className="add-condition-modal--gift-label">MINIMUM AMOUNT</label>
              <div className="add-condition-modal--amount-input">
                <span className="add-condition-modal--currency">$</span>
                <input
                  type="number"
                  placeholder="0.00"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                />
              </div>
            </div>
            <div className="add-condition-modal--gift-field">
              <label className="add-condition-modal--gift-label">DESIGNATION (OPTIONAL)</label>
              <input
                type="text"
                className="add-condition-modal--text-input"
                placeholder="Any designation"
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
              />
            </div>
          </div>
        );

      case 'portfolio':
        return (
          <div className="add-condition-modal--portfolio-options">
            <div className="add-condition-modal--portfolio-field">
              <label className="add-condition-modal--gift-label">PORTFOLIO TEAM</label>
              <select
                className="add-condition-modal--select"
                value={portfolioTeam}
                onChange={(e) => {
                  setPortfolioTeam(e.target.value);
                  setPortfolioStage('');
                  setPortfolioStageName('');
                }}
              >
                <option value="">{portfolioTeamsLoading ? 'Loading...' : 'Select team...'}</option>
                {(portfolioTeamsList || []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="add-condition-modal--portfolio-field">
              <label className="add-condition-modal--gift-label">PORTFOLIO STAGE</label>
              <select
                className="add-condition-modal--select"
                value={portfolioStage}
                onChange={(e) => {
                  setPortfolioStage(e.target.value);
                  const match = portfolioStages.find((s) => s.value === e.target.value);
                  setPortfolioStageName(match?.name || '');
                }}
                disabled={!portfolioTeam}
              >
                <option value="">{!portfolioTeam ? 'Select a team first' : 'Select stage...'}</option>
                {portfolioStages.map((stage) => (
                  <option key={stage.value} value={stage.value}>
                    {stage.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'savedSearch':
        return (
          <SearchableSelect
            options={savedSearches}
            value={savedSearch}
            onChange={setSavedSearch}
            placeholder={loading ? 'Loading...' : 'Search saved searches...'}
            className="add-condition-modal--searchable-select"
          />
        );

      case 'list':
        return (
          <SearchableSelect
            options={lists}
            value={list}
            onChange={setList}
            placeholder={loading ? 'Loading...' : 'Search lists...'}
            className="add-condition-modal--searchable-select"
          />
        );

      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} closeModal={handleClose} size="medium">
      <ModalHeader title="Add Condition" closeModal={handleClose} />
      <ModalBody scrollable={true}>
        <div className="add-condition-modal">
          <div className="add-condition-modal--groups">
            {CONDITION_GROUPS.map((group) => (
              <div key={group.category} className="add-condition-modal--group">
                <div className="add-condition-modal--category">{group.category}</div>
                {group.category === 'Email' && emailConditionsDisabled && (
                  <div className="add-condition-modal--email-notice">
                    Email conditions are only available when the previous step is an automated email.
                  </div>
                )}
                <div className="add-condition-modal--options">
                  {group.conditions.map((condition) => {
                    const isEmailCondition = EMAIL_CONDITION_IDS.has(condition.id);
                    const isDisabled = isEmailCondition && emailConditionsDisabled;
                    return (
                      <div key={condition.id} className="add-condition-modal--option-wrapper">
                        <label
                          className={`add-condition-modal--option${
                            selectedCondition?.id === condition.id ? ' is-selected' : ''
                          }${isDisabled ? ' is-disabled' : ''}`}
                          title={isDisabled ? 'Email conditions require a previous email step' : undefined}
                        >
                          <div className="add-condition-modal--radio">
                            <input
                              type="radio"
                              name="condition"
                              checked={selectedCondition?.id === condition.id}
                              onChange={() => !isDisabled && setSelectedCondition(condition)}
                              disabled={isDisabled}
                            />
                          </div>
                          <div className="add-condition-modal--option-content">
                            <span className="add-condition-modal--option-label">{condition.label}</span>
                            <span className="add-condition-modal--option-desc">{condition.description}</span>
                          </div>
                        </label>
                        {selectedCondition?.id === condition.id && condition.hasOptions && (
                          <div className="add-condition-modal--option-fields">{renderConditionOptions()}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </ModalBody>
      <ModalFooterWithButtons>
        <Button type="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          type="action"
          onClick={handleAddCondition}
          disabled={
            !selectedCondition ||
            (selectedCondition.optionType === 'interaction' && !interactionType?.value) ||
            (selectedCondition.optionType === 'portfolio' && (!portfolioTeam || !portfolioStage)) ||
            (selectedCondition.optionType === 'savedSearch' && !savedSearch?.value) ||
            (selectedCondition.optionType === 'list' && !list?.value)
          }
        >
          Add Condition
        </Button>
      </ModalFooterWithButtons>
    </Modal>
  );
};

AddConditionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddCondition: PropTypes.func.isRequired,
  previousStepType: PropTypes.string,
  existingCondition: PropTypes.shape({
    type: PropTypes.string,
    interactionType: PropTypes.string,
    minAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    designation: PropTypes.string,
    portfolioTeamId: PropTypes.string,
    portfolioStage: PropTypes.string,
    savedSearch: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    savedSearchListId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    list: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

export default AddConditionModal;
